import { ErrorHandler, Injectable, Injector, NgZone, inject } from '@angular/core';
import { ApolloError } from '@apollo/client/core';
import { NotificationService } from '../services/notification.service';

/**
 * Global Angular ErrorHandler.
 *
 * Catches all uncaught errors (sync throws, unhandled promise rejections routed
 * through Zone.js, and any Observable error not caught with an error: callback).
 *
 * Rules:
 *  - ApolloErrors / network errors → generic "Something went wrong" snackbar
 *  - ChunkLoadError / navigation cancellation → silently ignored (non-actionable)
 *  - Everything → console.error so it appears in logs/DevTools
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // Use Injector to avoid circular DI (ErrorHandler is provided very early).
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);

  handleError(error: unknown): void {
    const err = this.unwrap(error);

    console.error('[GlobalErrorHandler]', err);

    if (this.isSilent(err)) {
      return;
    }

    if (this.isNetworkOrApolloError(err)) {
      this.showToast($localize`:@@errorGeneric:Something went wrong. Please try again.`, $localize`:@@close:Close`);
    }
  }

  // Zone.js wraps errors in a plain object with a `rejection` key for promise rejections.
  private unwrap(error: unknown): unknown {
    if (error && typeof error === 'object' && 'rejection' in error) {
      return (error as { rejection: unknown }).rejection;
    }
    return error;
  }

  private isSilent(err: unknown): boolean {
    if (!(err instanceof Error)) {
      return false;
    }
    const msg = err.message ?? '';
    // Lazy chunk load failures are retried by the browser on next navigation.
    if (msg.includes('ChunkLoadError') || msg.includes('Loading chunk')) {
      return true;
    }
    // Angular router cancellation is normal during guard redirects.
    if (msg.includes('NavigationCanceling') || msg.includes('Navigation cancelled')) {
      return true;
    }
    return false;
  }

  private isNetworkOrApolloError(err: unknown): boolean {
    if (err instanceof ApolloError) {
      return true;
    }
    if (err instanceof Error) {
      const msg = err.message ?? '';
      if (msg.includes('Http failure') || msg.includes('NetworkError') || msg.includes('status code')) {
        return true;
      }
    }
    return false;
  }

  private showToast(message: string, action: string): void {
    // Run inside Angular zone so change detection fires for the snackbar.
    this.zone.run(() => {
      const toast = this.injector.get(NotificationService);
      toast.show(message, action, 4000);
    });
  }
}
